import * as THREE from "three";
import {
  candidateColor,
  displayName,
  type Slug,
} from "../../lib/candidates.ts";
import { candidateImage } from "../assets.ts";
import { flatWhite } from "./materials.ts";
import { createSprite } from "./sprite.ts";
import { textPlate } from "./text-plate.ts";
import { candidateWalls } from "./walls.ts";

const rgba = (hex: string, a: number): string => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

// A soft, low-opacity radial wash of the candidate's brand colour, mounted on
// the wall behind the portrait — the only colour in the black-on-white room.
function createHalo(color: string): THREE.Mesh {
  const c = document.createElement("canvas");
  c.width = c.height = 256;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  g.addColorStop(0, rgba(color, 0.45));
  g.addColorStop(0.55, rgba(color, 0.22));
  g.addColorStop(1, rgba(color, 0));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(6, 7),
    new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      depthWrite: false,
      depthTest: false, // never let the polygon-offset wall occlude it up close
      toneMapped: false,
    }),
  );
  mesh.userData.skipEdges = true; // line pass ignores it
  mesh.renderOrder = -1; // drawn before (behind) the portrait
  return mesh;
}

// One booth per candidate, mounted on its assigned wall (see walls.ts), facing
// the centre: the portrait high on the wall, a podium in front, a name caption.
// Each station Group carries `userData.slug`; `stations` maps slug → it.
export function createStations(): {
  group: THREE.Group;
  stations: Map<Slug, THREE.Object3D>;
} {
  const group = new THREE.Group();
  group.name = "stations";
  const stations = new Map<Slug, THREE.Object3D>();

  for (const { slot, slug } of candidateWalls) {
    const station = new THREE.Group();
    station.position.set(slot.x, 0, slot.z);
    station.lookAt(0, 0, 0); // +Z toward the centre
    station.userData.slug = slug;

    const halo = createHalo(candidateColor(slug));
    halo.position.set(0, 3.4, 0.16); // between the wall and the portrait
    station.add(halo);

    const portrait = createSprite(candidateImage(slug), 4.8);
    portrait.mesh.position.set(0, 3.4, 0.3);
    station.add(portrait.mesh);

    const podium = new THREE.Mesh(
      new THREE.BoxGeometry(2, 1, 1.4),
      flatWhite(),
    );
    podium.position.set(0, 0.5, 2.2);
    station.add(podium);

    const plate = textPlate(displayName(slug), 2.2, 0.55);
    plate.position.set(0, 0.55, 2.95);
    station.add(plate);

    group.add(station);
    stations.set(slug, station);
  }

  return { group, stations };
}
